'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import { Children, cloneElement, forwardRef, isValidElement, useState } from "react";
import { cva } from "class-variance-authority";
import { Container as Root } from "../Container/index.js";
import { cn } from "../../common/helpers.js";
import useScreenMediaQuery from "../../common/hooks/useScreenMediaQuery.js";
const classesWrapper = cva([
    'flex',
    'flex-row',
    'flex-wrap',
    'gap-x-5',
    'gap-y-5'
], {
    variants: {
        containerSize: {
            12: [
                'col-span-12'
            ],
            10: [
                'col-span-12',
                'lg:col-span-10'
            ],
            8: [
                'col-span-12',
                'lg:col-span-10',
                'xl:col-span-8'
            ]
        },
        containerAlignment: {
            left: [
                'col-start-1'
            ],
            center: [
                'col-start-1'
            ]
        },
        cardsPerRow: {
            2: [
                '[&>*]:w-grid-1',
                'sm:[&>*]:w-grid-2'
            ],
            3: [
                '[&>*]:w-grid-1',
                'sm:[&>*]:w-grid-2',
                'lg:[&>*]:w-grid-3'
            ],
            4: [
                '[&>*]:w-grid-1',
                'sm:[&>*]:w-grid-2',
                'lg:[&>*]:w-grid-3',
                'xl:[&>*]:w-grid-4'
            ]
        }
    },
    compoundVariants: [
        {
            containerSize: 10,
            containerAlignment: 'center',
            className: [
                'lg:col-start-2'
            ]
        },
        {
            containerSize: 8,
            containerAlignment: 'center',
            className: [
                'lg:col-start-2',
                'xl:col-start-3'
            ]
        }
    ]
});
/**
 * Card Accordion Block - [API](https://scl.sharp.com/?path=/docs/components-cardaccordionblock) | [DSM](https://sharphealthcare.notion.site/Card-accordion-block-0a1cf6e8b4d74b1dbdd79caa23934e4a)
 *
 * @example
 *
 * <CardAccordionBlock cardsPerRow={3}>
 *   <CardAccordion ... />
 *   <CardAccordion ... />
 *   <CardAccordion ... />
 * </CardAccordionBlock>
 */ const CardAccordionBlock = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { as, cardsPerRow = 3, itemsPerRow, cardAlignment, containerSize = 12, containerAlignment = 'left', slots = {}, slotProps = {}, className, children } = props, rest = _object_without_properties(props, [
        "as",
        "cardsPerRow",
        "itemsPerRow",
        "cardAlignment",
        "containerSize",
        "containerAlignment",
        "slots",
        "slotProps",
        "className",
        "children"
    ]);
    var _slots_container;
    const Container = (_slots_container = slots.container) !== null && _slots_container !== void 0 ? _slots_container : 'div';
    var _slotProps_container;
    const _ref = (_slotProps_container = slotProps.container) !== null && _slotProps_container !== void 0 ? _slotProps_container : {}, { className: containerClassName } = _ref, containerProps = _object_without_properties(_ref, [
        "className"
    ]);
    const [open, setOpen] = useState(false);
    const isScreenAtLeastSm = useScreenMediaQuery('sm');
    const collapsibleProperty = {
        collapsible: {
            open,
            onOpenChange: (state)=>setOpen(state)
        }
    };
    return /*#__PURE__*/ _jsx(Root, _object_spread_props(_object_spread({
        className: cn('grid grid-cols-12', className)
    }, rest), {
        as: as,
        ref: ref,
        children: /*#__PURE__*/ _jsx(Container, _object_spread_props(_object_spread({
            className: cn(classesWrapper({
                cardsPerRow: itemsPerRow !== null && itemsPerRow !== void 0 ? itemsPerRow : cardsPerRow,
                containerAlignment,
                containerSize
            }), containerClassName)
        }, containerProps), {
            children: Children.map(children, (child)=>{
                if (/*#__PURE__*/ isValidElement(child)) {
                    return /*#__PURE__*/ cloneElement(child, _object_spread({}, isScreenAtLeastSm ? collapsibleProperty : {}));
                }
                return child;
            })
        }))
    }));
});
export { CardAccordionBlock };
